import {
  ApolloClient,
  ApolloProvider,
  createHttpLink,
  from,
  InMemoryCache,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { Auth } from 'aws-amplify'
import i18n from 'i18next'
import React, { useState, useEffect } from 'react'
import { hydrate } from 'react-dom'
import { createRoot } from 'react-dom/client'
import { initReactI18next } from 'react-i18next'
import { BrowserRouter } from 'react-router-dom'
import App from './App'
import configUrl from './config'
import resources from './i18n'
import config from './config/aws'
import Login from './pages/Login/Login'
import jwtDecode from 'jwt-decode'

import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import LiveContext from './components/Context/LiveContext'
import './index.css'
import LivePreview from './pages/LivePreview/LivePreview'
import reportWebVitals from './reportWebVitals'
import * as serviceWorker from './serviceWorker'

const isLocal =
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1'

const httpLink = createHttpLink({
  uri: configUrl.GRAPHQL_URL,
})

const authLink = setContext(async (_, { headers }) => {
  try {
    const session = await Auth.currentSession()
    const idToken = session.getIdToken().getJwtToken()
    return {
      headers: {
        ...headers,
        authorization: `Bearer ${idToken}`,
      },
    }
  } catch (e) {
    console.log(e)
    return {
      headers,
    }
  }
})

i18n
  .use(initReactI18next) // passes i18n down to react-i18next
  .init({
    resources,
    lng: localStorage.getItem('key') || 'fr',
    fallbackLng: 'fr',

    interpolation: {
      escapeValue: false,
    },
  })

const Root = () => {
  const [isLivePreviewOpened, setLivePreviewOpened] = useState(false)
  const [isLivePreviewMinimized, setLivePreviewMinimized] = useState(false)
  const [isUserLogged, setUserLogged] = useState(false)

  const [isLoginOpened, setLoginOpened] = useState(false)
  const [idWatcher, setIdWatcher] = useState(null)
  const [userRole, setUserRole] = useState('ubiqeyes_watcher')

  const [localDataTrack, setLocalDataTrack] = useState(null)

  let liveContextValue = {
    isLivePreviewMinimized,
    isLivePreviewOpened,
    isUserLogged,
    isLoginOpened,
    idWatcher,
    localDataTrack,
    userRole,
    setIdWatcher,
    setUserLogged,
    toggleLoginOpened: setLoginOpened,
    toggleLivePreview: setLivePreviewOpened,
    toggleLivePreviewMinimized: setLivePreviewMinimized,
    setLocalDataTrack,
    setUserRole,
  }

  useEffect(() => {
    if (localStorage.getItem('access_token')) {
      const userToken = localStorage.getItem('access_token')
      setUserRole(jwtDecode(userToken).role)
    }
  }, [])

  const errorLink = onError(
    ({ graphQLErrors, networkError, operation, forward }) => {
      if (graphQLErrors)
        graphQLErrors.forEach(({ message, locations, path }) => {
          console.log(
            `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
          )
          ;('null value in column "id_watcher" of relation "request" violates not-null constraint')
          switch (message) {
            case 'jwt expired':
              ;(async () => {
                let newIdToken
                const refreshSession = await fetch(
                  config.aws.refreshSessionUrl,
                  {
                    headers: {
                      'X-Amz-Target':
                        'AWSCognitoIdentityProviderService.InitiateAuth',
                      'Content-Type': 'application/x-amz-json-1.1',
                    },
                    mode: 'cors',
                    cache: 'no-cache',
                    method: 'POST',
                    body: JSON.stringify({
                      ClientId: config.aws.userPoolWebClientId,
                      AuthFlow: 'REFRESH_TOKEN_AUTH',
                      AuthParameters: {
                        REFRESH_TOKEN: localStorage.getItem('refresh_token'),
                      },
                    }),
                  }
                )
                const response = await refreshSession.json()

                const userTokenResponse =
                  response?.AuthenticationResult?.IdToken

                newIdToken = userTokenResponse
                localStorage.setItem('access_token', userTokenResponse)
                const headers = operation.getContext().headers
                operation.setContext({
                  headers: {
                    ...headers,
                    authorization: `Bearer ${newIdToken}`,
                  },
                })
                return forward(operation).subscribe()
              })()
              break
            case 'null value in column "id_watcher" of relation "request" violates not-null constraint':
              setLoginOpened(true)
          }
        })

      if (networkError) console.log(`[Network error]: ${networkError}`)
    }
  )

  const client = new ApolloClient({
    connectToDevTools: isLocal ? true : false, // ternaire pour dev mettre true sinon false
    link: from([errorLink, authLink.concat(httpLink)]),
    cache: new InMemoryCache(),
  })

  return (
    <ApolloProvider client={client}>
      <LiveContext.Provider value={liveContextValue}>
        <BrowserRouter>
          <App />
          <ToastContainer
            position="top-center"
            autoClose={2000}
            hideProgressBar={true}
            newestOnTop={false}
            closeOnClick={true}
            rtl={false}
            pauseOnFocusLoss
            draggable={true}
            style={{ width: 'auto' }}
          />
          <Login />
          <LivePreview />
        </BrowserRouter>
      </LiveContext.Provider>
    </ApolloProvider>
  )
}

const rootElement = document.getElementById('root')
const root = createRoot(rootElement) // createRoot(container!) if you use TypeScript
if (rootElement.hasChildNodes()) {
  hydrate(<Root />, rootElement)
} else {
  root.render(<Root />)
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.register()
